import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign, Download, Receipt, Zap, TrendingUp, FileX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

interface UploadFee {
  id: string;
  fee_type: string;
  amount_usd: number;
  paid: boolean;
  paid_at: string | null;
  description: string | null;
  created_at: string;
  works?: { title: string } | null;
}

interface SliceTransaction {
  id: string;
  amount: number;
  transaction_type: string;
  description: string | null;
  created_at: string;
  works?: { title: string } | null;
}

interface CollaborationEarning {
  contract_id: string;
  work_title: string;
  owner_name: string;
  role_label: string;
  split_percentage: number;
  slices_earned: number;
}

const CollaborationEarningsSection = () => {
  const [works, setWorks] = useState<CollaborationEarning[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .rpc('get_collaborator_works')
      .then(({ data, error }) => {
        if (!error) setWorks((data || []) as CollaborationEarning[]);
      })
      .then(
        () => setLoading(false),
        () => setLoading(false)
      );
  }, []);

  const totalEarned = works.reduce((sum, w) => sum + Number(w.slices_earned || 0), 0);

  if (loading) {
    return (
      <Card className='p-4 md:p-6 partner-card'>
        <Skeleton className='h-6 w-40 mb-4' />
        <Skeleton className='h-20 w-full' />
      </Card>
    );
  }
  if (works.length === 0) return null;

  return (
    <Card className='p-4 md:p-6 partner-card'>
      <div className='flex items-center justify-between mb-4'>
        <h3 className='text-base md:text-lg font-semibold'>Shared Works — Collaboration Earnings</h3>
        <div className='flex items-center gap-1 text-sm font-semibold'>
          <Zap className='h-4 w-4 text-primary' />
          <span>{totalEarned.toLocaleString()} slices total</span>
        </div>
      </div>
      <p className='text-xs text-muted-foreground mb-4'>
        Your percentage share of slice donations on works owned by other studios. Splits applied at payout —
        no real-time distribution.
      </p>
      <div className='space-y-3'>
        {works.map(w => (
          <div
            key={w.contract_id}
            className='flex items-center justify-between p-3 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors'
          >
            <div className='flex-1 min-w-0'>
              <p className='text-sm font-medium truncate'>{w.work_title}</p>
              <p className='text-xs text-muted-foreground mt-0.5'>
                {w.owner_name} · {w.role_label} · {w.split_percentage}% share
              </p>
            </div>
            <div className='flex items-center gap-1 ml-4 flex-shrink-0'>
              <Zap className='h-3 w-3 text-primary' />
              <p className='text-sm font-semibold'>{Number(w.slices_earned).toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const Revenue = () => {
  const { publisher } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploadFees, setUploadFees] = useState<UploadFee[]>([]);
  const [sliceTransactions, setSliceTransactions] = useState<SliceTransaction[]>([]);

  useEffect(() => {
    if (publisher?.id) {
      fetchRevenueData(publisher.id);
    }
  }, [publisher]);

  const fetchRevenueData = async (publisherId: string) => {
    setLoading(true);
    try {
      // Fetch upload fees for this publisher
      const { data: fees, error: feesError } = await supabase
        .from('content_upload_fees')
        .select('id, fee_type, amount_usd, paid, paid_at, description, created_at, works(title)')
        .eq('publisher_id', publisherId)
        .order('created_at', { ascending: false });

      if (feesError) console.error('Error fetching upload fees:', feesError);
      else setUploadFees((fees as unknown as UploadFee[]) || []);

      // Get publisher's work IDs (slice_transactions has no direct publisher FK)
      const { data: works } = await supabase
        .from('works')
        .select('id, title')
        .eq('publisher_id', publisherId);

      const workIds = (works || []).map((w: { id: string }) => w.id);
      const workTitles = Object.fromEntries((works || []).map((w: any) => [w.id, w.title]));

      if (workIds.length > 0) {
        const { data: transactions, error: txError } = await supabase
          .from('slice_transactions')
          .select('id, amount, transaction_type, description, created_at, work_id')
          .in('work_id', workIds)
          .order('created_at', { ascending: false });

        if (txError) console.error('Error fetching slice transactions:', txError);
        else {
          // Map work titles onto transactions since there's no FK join
          const mapped = (transactions || []).map((tx: any) => ({
            ...tx,
            works: tx.work_id ? { title: workTitles[tx.work_id] || null } : null,
          }));
          setSliceTransactions(mapped as SliceTransaction[]);
        }
      }
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      toast.error('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  const totalFees = uploadFees.reduce((sum, f) => sum + (f.amount_usd || 0), 0);
  const paidFees = uploadFees.filter(f => f.paid).reduce((sum, f) => sum + (f.amount_usd || 0), 0);
  const outstandingFees = totalFees - paidFees;
  const totalSlices = sliceTransactions.reduce(
    (sum, t) => sum + Math.abs(t.amount || 0),
    0
  );

  const feeTypeLabel = (type: string) =>
    type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  if (loading) {
    return (
      <div className='p-4 md:p-6 space-y-4 md:space-y-6 text-foreground'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
          <div>
            <h1 className='text-2xl md:text-3xl font-bold'>Revenue</h1>
            <p className='text-muted-foreground text-sm md:text-base'>Track earnings and payment history</p>
          </div>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6'>
          {[1, 2, 3].map(i => (
            <Card key={i} className='p-4 md:p-6 partner-card'>
              <Skeleton className='h-4 w-24 mb-3' />
              <Skeleton className='h-8 w-32' />
            </Card>
          ))}
        </div>
        <Card className='p-4 md:p-6 partner-card'>
          <Skeleton className='h-6 w-40 mb-4' />
          <Skeleton className='h-20 w-full' />
        </Card>
      </div>
    );
  }

  return (
    <div className='p-4 md:p-6 space-y-4 md:space-y-6 text-foreground'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-2xl md:text-3xl font-bold'>Revenue</h1>
          <p className='text-muted-foreground text-sm md:text-base'>
            Track earnings and payment history
          </p>
        </div>
        <Button
          variant='outline'
          className='border-border/60 text-foreground hover:bg-muted/60 w-full sm:w-auto'
          onClick={() => {
            // TODO: implement CSV export when billing pipeline is ready
            toast.info('Export coming soon');
          }}
        >
          <Download className='h-4 w-4 mr-2' />
          Export Report
        </Button>
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6'>
        <Card className='p-4 md:p-6 partner-card'>
          <div className='flex items-center space-x-3 md:space-x-4'>
            <Receipt className='h-7 w-7 md:h-8 md:w-8 text-primary flex-shrink-0' />
            <div className='min-w-0'>
              <p className='text-xs md:text-sm font-medium text-muted-foreground'>Upload Fees (Total)</p>
              {uploadFees.length > 0 ? (
                <p className='text-xl md:text-2xl font-semibold truncate'>${totalFees.toFixed(2)}</p>
              ) : (
                <p className='text-sm text-muted-foreground mt-1'>No fees yet</p>
              )}
            </div>
          </div>
        </Card>

        <Card className='p-4 md:p-6 partner-card'>
          <div className='flex items-center space-x-3 md:space-x-4'>
            <DollarSign className='h-7 w-7 md:h-8 md:w-8 text-publisher-300 flex-shrink-0' />
            <div className='min-w-0'>
              <p className='text-xs md:text-sm font-medium text-muted-foreground'>Outstanding Fees</p>
              {outstandingFees > 0 ? (
                <p className='text-xl md:text-2xl font-semibold truncate text-amber-500'>${outstandingFees.toFixed(2)}</p>
              ) : (
                <p className='text-sm text-muted-foreground mt-1'>
                  {uploadFees.length > 0 ? 'All paid ✓' : 'None'}
                </p>
              )}
            </div>
          </div>
        </Card>

        <Card className='p-4 md:p-6 partner-card'>
          <div className='flex items-center space-x-3 md:space-x-4'>
            <Zap className='h-7 w-7 md:h-8 md:w-8 text-theatre-300 flex-shrink-0' />
            <div className='min-w-0'>
              <p className='text-xs md:text-sm font-medium text-muted-foreground'>Slices Received</p>
              {sliceTransactions.length > 0 ? (
                <p className='text-xl md:text-2xl font-semibold truncate'>{totalSlices.toLocaleString()}</p>
              ) : (
                <p className='text-sm text-muted-foreground mt-1'>No slices yet</p>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Stripe payouts note */}
      <Card className='p-4 md:p-6 partner-card border-primary/20 bg-primary/5'>
        <div className='flex items-start gap-3'>
          <TrendingUp className='h-5 w-5 text-primary mt-0.5 flex-shrink-0' />
          <div>
            <p className='text-sm font-medium'>Stripe Payouts Coming Soon</p>
            <p className='text-xs text-muted-foreground mt-1'>
              Slice revenue tracking is live. Cash payouts via Stripe Connect will be available in Sprint 3.2 —
              your earned slices are accumulating and will convert to payouts once connected.
            </p>
          </div>
        </div>
      </Card>

      {/* Upload Fees */}
      <Card className='p-4 md:p-6 partner-card'>
        <h3 className='text-base md:text-lg font-semibold mb-4'>Upload Fees</h3>
        {uploadFees.length > 0 ? (
          <div className='space-y-3'>
            {uploadFees.map(fee => (
              <div
                key={fee.id}
                className='flex items-center justify-between p-3 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors'
              >
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <p className='text-sm font-medium'>{fee.works?.title || feeTypeLabel(fee.fee_type)}</p>
                    <Badge
                      variant={fee.paid ? 'default' : 'secondary'}
                      className={fee.paid ? 'bg-green-500/10 text-green-500 text-xs' : 'text-xs'}
                    >
                      {fee.paid ? 'Paid' : 'Unpaid'}
                    </Badge>
                  </div>
                  <p className='text-xs text-muted-foreground mt-0.5'>
                    {feeTypeLabel(fee.fee_type)}
                    {fee.paid_at && ` · Paid ${new Date(fee.paid_at).toLocaleDateString()}`}
                  </p>
                </div>
                <p className='text-sm font-semibold ml-4 flex-shrink-0'>${fee.amount_usd.toFixed(2)}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className='text-center py-8 text-muted-foreground'>
            <FileX className='h-10 w-10 mx-auto mb-3 opacity-40' />
            <p className='text-sm font-medium'>No upload fees yet</p>
            <p className='text-xs mt-1'>Fees appear here when you upload content</p>
          </div>
        )}
      </Card>

      {/* Slice Transactions */}
      <Card className='p-4 md:p-6 partner-card'>
        <h3 className='text-base md:text-lg font-semibold mb-4'>Slice Transactions</h3>
        {sliceTransactions.length > 0 ? (
          <div className='space-y-3'>
            {sliceTransactions.map(tx => (
              <div
                key={tx.id}
                className='flex items-center justify-between p-3 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors'
              >
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium'>
                    {tx.works?.title || tx.description || feeTypeLabel(tx.transaction_type)}
                  </p>
                  <p className='text-xs text-muted-foreground mt-0.5'>
                    {tx.transaction_type.replace(/_/g, ' ')} · {new Date(tx.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className='flex items-center gap-1 ml-4 flex-shrink-0'>
                  <Zap className='h-3 w-3 text-primary' />
                  <p className='text-sm font-semibold'>{Math.abs(tx.amount)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className='text-center py-8 text-muted-foreground'>
            <Zap className='h-10 w-10 mx-auto mb-3 opacity-40' />
            <p className='text-sm font-medium'>No slice transactions yet</p>
            <p className='text-xs mt-1'>Viewer slice donations will appear here</p>
          </div>
        )}
      </Card>

      {/* Collaboration earnings on works owned by other studios */}
      <CollaborationEarningsSection />
    </div>
  );
};

export default Revenue;
